import React, { Reducer, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useSnackbar } from 'notistack';
import { Button, Chip, Typography } from '@mui/material';
import { useAuthContext } from 'AuthContext';
import { useMeetingHubContext } from '../../MeetingHubContext';
import reducer, {
  initialState,
  VotingTableAction,
  VotingTableActionType,
  VotingTableState,
} from './VotingTableReducer';
import MeetingService from 'services/MeetingService';
import Table from 'components/shared/Table';
import { VoteValueType } from 'store/enums/VoteTypes';
import { Attendee, Vote } from 'store/types/Meeting';
import { SortConfig, TableColumn } from 'store/types/Table';
import { defaultSnackbarErrorProps } from 'util/layout';
import { voteValueColor, voteValueLabels } from 'util/meeting';

import styles from './VotingTable.module.scss';

interface VotingTableProps {
  motionId: string;
  disableVoting: boolean;
  voters: Attendee[];
  votesByAttendeeId: Map<string, Vote>;
}

const VotingTable: React.FunctionComponent<VotingTableProps> = ({
  motionId,
  disableVoting,
  voters,
  votesByAttendeeId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isStaff } = useAuthContext();
  const { meetingId, isPresidingChair } = useMeetingHubContext();
  const [{ list = [], sort }, dispatch] = useReducer<Reducer<VotingTableState, VotingTableAction>>(
    reducer,
    initialState
  );

  const canCastVote = !isStaff && !isPresidingChair;

  useEffect(() => {
    if (voters) {
      dispatch({
        type: VotingTableActionType.SetInitialList,
        payload: { initialList: voters },
      });
    }
  }, [voters]);

  const onSortChange = useCallback((newSort: SortConfig) => {
    dispatch({ type: VotingTableActionType.UpdateSort, payload: { sort: newSort } });
  }, []);

  const castVote = useCallback(
    (attendee: Attendee, value: VoteValueType) => {
      const vote: Vote = {
        meetingId,
        motionId,
        attendeeId: attendee.id,
        attendeeName: attendee.name,
        affiliate: attendee.affiliate ?? '',
        value,
      };

      MeetingService.saveVote(vote)
        .then((vote) => {
          enqueueSnackbar(`Vote cast: ${voteValueLabels[vote.value]}`, { variant: 'success' });
        })
        .catch((error: string) => {
          enqueueSnackbar(`Failed to cast vote`, defaultSnackbarErrorProps);
          console.error('Error casting vote:', error);
        });
    },
    [enqueueSnackbar, meetingId, motionId]
  );

  const columns: Array<TableColumn<Attendee>> = useMemo(() => {
    const result: Array<TableColumn<Attendee>> = [
      {
        dataIndex: 'affiliate',
        label: <Typography fontWeight="bold">Voting Interest</Typography>,
        render: (_, record) => <Typography>{record.affiliate}</Typography>,
      },
      {
        dataIndex: 'company',
        label: <Typography fontWeight="bold">Company</Typography>,
        render: (_, record) => <Typography>{record.company}</Typography>,
      },
      {
        dataIndex: 'name',
        label: <Typography fontWeight="bold">Name</Typography>,
        render: (_, record) => <Typography>{record.name}</Typography>,
      },
      {
        key: 'vote',
        align: 'center',
        label: <Typography fontWeight="bold">Vote</Typography>,
        render: (_, record) => {
          const showVotingOptions = canCastVote || (record.isManuallyAdded && isStaff);
          const existingVote = votesByAttendeeId.get(record.id)?.value;

          return showVotingOptions ? (
            <div className={styles.buttonGroup}>
              <Button
                color={existingVote === VoteValueType.Yes ? 'secondary' : 'success'}
                variant={existingVote === VoteValueType.Yes ? 'contained' : 'outlined'}
                disabled={disableVoting}
                onClick={() => castVote(record, VoteValueType.Yes)}
              >
                {voteValueLabels[VoteValueType.Yes]}
              </Button>
              <Button
                color={existingVote === VoteValueType.No ? 'secondary' : 'error'}
                variant={existingVote === VoteValueType.No ? 'contained' : 'outlined'}
                disabled={disableVoting}
                onClick={() => castVote(record, VoteValueType.No)}
              >
                {voteValueLabels[VoteValueType.No]}
              </Button>
              <Button
                color={existingVote === VoteValueType.Abstain ? 'secondary' : 'warning'}
                variant={existingVote === VoteValueType.Abstain ? 'contained' : 'outlined'}
                disabled={disableVoting}
                onClick={() => castVote(record, VoteValueType.Abstain)}
              >
                {voteValueLabels[VoteValueType.Abstain]}
              </Button>
            </div>
          ) : existingVote ? (
            <Chip
              color={disableVoting ? 'default' : voteValueColor[existingVote]}
              label={voteValueLabels[existingVote]}
            />
          ) : (
            <Chip label={'Waiting on vote'} />
          );
        },
      },
    ];

    return result;
  }, [canCastVote, castVote, disableVoting, isStaff, votesByAttendeeId]);

  return (
    <Table
      className={styles.table}
      columns={columns}
      list={list}
      sort={sort}
      onSortChange={onSortChange}
      noResultsText="No current attendance"
    />
  );
};

export default VotingTable;
